import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { App, Produktlinie } from '@ps20-frontend/frontend-models';
import cloneDeep from 'lodash/cloneDeep';
import { Produktlinienname } from '@ps20/bff-models';
import { GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';
import { VersicherungsSummeGrenzen } from '../model/versicherungssummen';
import { selectVorschaedenUeberschrittenProduktlinie } from '../../app/shared/selectors/vorschaeden.selector';
import { VersicherungssummeForLinie } from '../action-types/versicherungssummenResponseReceived';

export function* watchproduktlinienSaga(): SagaIterator {
  yield takeLatest(
    VhvActions.VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED,
    safeSaga(updateProduktlinienStatus, App.VHV),
  );
  yield takeLatest(VhvActions.VORSCHAEDEN_CHANGED, safeSaga(updateProduktlinienStatus, App.VHV));
  yield takeLatest(
    VhvActions.VORSCHAEDEN_FELS_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV),
  );
  yield takeLatest(
    VhvActions.VORSCHAEDEN_FAHRRAD_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV),
  );
  yield takeLatest(
    VhvActions.VERSICHERUNGS_OBJEKT_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV),
  );
}

export function* updateProduktlinienStatus(): SagaIterator {
  const vorschaedenUeberschritten: boolean = yield select(
    selectVorschaedenUeberschrittenProduktlinie,
  );
  const appState: IAppState = cloneDeep(yield select());
  const versSummen: Array<VersicherungssummeForLinie> = appState.versicherungssummen;
  const vorschaeden: boolean =
    appState.vorschaeden && appState.vorschaeden.vorhanden && vorschaedenUeberschritten;
  const unterversicherungsverzicht: boolean = appState.unterversicherungsverzicht;
  const produktlinien = appState.produkt.produktlinien;

  toggleProduktlinienActiveState(
    versSummen,
    produktlinien,
    vorschaeden,
    unterversicherungsverzicht,
  );
  handleNichtStaendigBewohnt(produktlinien, appState);

  resetToDefaultProduktlinieIfNecessary(produktlinien);
  const changeProduktlinienAction = new VhvActions().produktlinienChanged(produktlinien);
  yield put(changeProduktlinienAction);

  if (noProduktlinieSelected(produktlinien)) {
    const globalPut = GlobalPut.getInstance();
    yield call(
      globalPut.put.bind(globalPut),
      new GlobalActions().gesamtpreisChanged(0, false, App.VHV, appState.angebotsId),
    );
  }
}

function isProduktlinieSOrM(versSumme: VersicherungssummeForLinie): boolean {
  return (
    versSumme.produktlinie === Produktlinienname.BASIS ||
    versSumme.produktlinie === Produktlinienname.SMART
  );
}

function shouldDeactivateLinieSOrM(
  versSumme: number,
  vorschaedenVorhanden: boolean,
  unterversicherungsverzicht: boolean,
) {
  return (
    versSumme > VersicherungsSummeGrenzen.MIN_VERSSUMME_FUER_S_AND_M ||
    vorschaedenVorhanden ||
    unterversicherungsverzicht
  );
}

function toggleProduktlinienActiveState(
  versSummen: Array<VersicherungssummeForLinie>,
  produktlinien: Produktlinie[],
  vorschaedenVorhanden: boolean,
  unterversicherungsverzicht: boolean,
) {
  produktlinien.forEach(produktlinie => (produktlinie.deactivated = false));

  versSummen.forEach(versSumme => {
    if (
      isProduktlinieSOrM(versSumme) &&
      shouldDeactivateLinieSOrM(versSumme.summe, vorschaedenVorhanden, unterversicherungsverzicht)
    ) {
      deactivateProduktlinie(produktlinien, versSumme.produktlinie);
    }
  });
}

export function resetToDefaultProduktlinieIfNecessary(produktlinien: Produktlinie[]) {
  const produktlinieL = produktlinien.find(
    produktlinie => produktlinie.produktName === Produktlinienname.KOMFORT,
  );
  const isSomeProduktlinieSelected = produktlinien.some(
    produktlinie => produktlinie.selected === true,
  );

  if (!isSomeProduktlinieSelected && produktlinieL.deactivated === false) {
    produktlinieL.selected = true;
  }
}

function handleNichtStaendigBewohnt(produktlinien: Array<Produktlinie>, appState: IAppState) {
  const nichtStaendigBewohnt = !appState.versObjekt?.staendigBewohnt;
  const versSumme: number = appState.vertragsoptionen?.versicherungssumme;

  if (nichtStaendigBewohnt) {
    deactivateProduktlinie(produktlinien, Produktlinienname.PREMIUM);
  }
  const shouldDeactivateAllWithoutKomfort =
    (nichtStaendigBewohnt &&
      versSumme > VersicherungsSummeGrenzen.MAX_VERSSUMME_NICHT_STAENDIG_BEWOHNT) ||
    (!nichtStaendigBewohnt && versSumme > VersicherungsSummeGrenzen.MAX_VERSSUMME);
  if (shouldDeactivateAllWithoutKomfort) {
    [Produktlinienname.BASIS, Produktlinienname.SMART, Produktlinienname.PREMIUM].forEach(
      productLine => deactivateProduktlinie(produktlinien, productLine),
    );
    const productLineL = produktlinien.find(
      productLine => productLine.produktName == Produktlinienname.KOMFORT,
    );
    productLineL.selected = true;
    productLineL.deactivated = false;
  }
}

export function deactivateProduktlinie(
  produktlinien: Produktlinie[],
  produktlineType: Produktlinienname,
) {
  produktlinien.forEach(tarifItem => {
    if (produktlineType === tarifItem.produktName) {
      tarifItem.deactivated = true;
      tarifItem.selected = false;
    }
  });
}

export function activateProduktlinie(
  produktlinien: Produktlinie[],
  produktlineType: Produktlinienname,
) {
  produktlinien.forEach(tarifItem => {
    if (produktlineType === tarifItem.produktName) {
      tarifItem.deactivated = false;
    }
  });
}

function noProduktlinieSelected(produktlinien: Array<Produktlinie>): boolean {
  return !produktlinien.some(produktlinie => produktlinie.selected);
}
